import { BankOutlined, FormOutlined, SettingOutlined } from "@ant-design/icons";
import { Col, Grid, message, Row, Space, Spin, Tag, Typography } from "antd";
import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useLocation, useParams } from "react-router-dom";
import { useGetOrganization } from "../../../../api/v1";
import { Apartment, Bed, History, Measure, Residential } from "../../../../assets/svg";
import { Booking } from "../../../../types/bookings";
import { formatString } from "../../../../utils/formatString";
import { statusLabel } from "../../../../utils/media";
import { checkUserRole } from "../../../../utils/userRoles";
import { Popup, PopupConfirm } from "../../../atoms/Popup";
import { formatDate, formatDateRange } from "../../../libs/utils";
import RejectPopup from "../../LeasingFormNew/components/RejectionPopup";
import { ItemCard } from "../components";
import { getBookingDetail, updateBooking } from "../ducks/actions";
import { EarlyTerminationApprovalActions } from "./LeaseActions";
import { RentPerYear } from "./RentPerYear";
import { ApprovalHeader, CurrencyExchangeIcon, leaseExecutiveName } from "./utils";
import { usePendingRequestsActions } from "../../PendingApprovalRequets/api/useRefreshPendingRequests";

interface IProps {
  booking: any;
  renewal?: boolean;
  handleChangeHistory?: (key: string) => void;
  stateRenewal?: boolean;
  setShowCheckList?: (boolean) => void;
  isRenewalToRenewal?: boolean;
}

const { Title } = Typography;

type ActionChip = {
  key: string;
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  marginLeft: number | string;
};

const BookingDetailsCard = (props: IProps) => {
  const { renewalStatus, leaseType } = props?.booking as Booking;

  const dispatch: any = useDispatch();
  const { state } = useLocation();
  const { leaseId = "" } = useParams();
  const { refreshPendingRequests } = usePendingRequestsActions();

  const { organization } = useGetOrganization();
  const screens = Grid.useBreakpoint();

  const isBooking = props?.booking && Object.keys(props?.booking)?.length > 0;

  const rowStyle = { marginLeft: 0, marginRight: 0 };
  const rowStyleWithTopmargin = { ...rowStyle, ...{ marginTop: "16px" } };
  const isLeaseManager = Object.keys(props.booking?.leaseManager || {}).length > 0;

  const renewal_accepted = props?.booking?.leaseStatusHistory?.find(
    (status) => status?.status === "renewal_accepted" || status?.status === "renewal_confirmed",
  );

  const diffLeaseStatus = () => {
    if (props?.stateRenewal || props?.booking?.leaseType === "renewal") {
      if (props.booking?.leaseStatus === "awaiting_documents" && props.booking?.renewalStatus === "renewal_declined")
        return props.booking?.renewalStatus;
      else if (props?.isRenewalToRenewal) {
        //  when creating new renewal from existing renewal
        return "To Review";
      } else if (props?.booking?.leaseType === "renewal" && !renewal_accepted) {
        return props.booking?.renewalStatus;
      } else if (
        (props.booking?.leaseStatus === "awaiting_renewal_fee" || props.booking?.leaseStatus === "renewal_sent") &&
        props.booking?.renewalStatus === "renewal_declined"
      )
        return "Renewal Declined";
      else if (props.booking?.leaseStatus === "renewal_sent" && props.booking?.renewalStatus === "sent_for_approval")
        return props.booking?.renewalStatus;
      else if (props.booking?.leaseStatus === "renewal_sent" && props.booking?.renewalStatus === "ready_to_send")
        return props.booking?.renewalStatus;
      else if (props.booking?.leaseStatus === "renewal_sent" && props.booking?.renewalStatus === "sent")
        return "Sent To Tenant";
      else if (props.booking?.leaseStatus === "renewal_sent" && props.booking?.renewalStatus === "to_review")
        return "To Review";
      else return props.booking?.leaseStatus;
    } else {
      return props.booking?.leaseStatus;
    }
  };
  const [modalvisible, setModalvisible] = useState(false);
  const [modalTrigger, setModalTrigger] = useState(0);

  const label = () => {
    if (props?.renewal || props?.booking?.leaseType === "renewal") return "Renewal Id";
    else return "Booking Id";
  };

  const moveOutSummeryApproval = () => {
    let body = {
      settlementApproval: "approved",
      onlyBooking: true,
    };
    PopupConfirm({
      title: "Are you sure you want to approve move-out summary?",
      content: "",
      onOk: async () => {
        try {
          const response: any = await dispatch(updateBooking({ leaseId, body: body }));

          if (response?.data?.code === 200) {
            message.success(response?.data?.message);
            await refreshPendingRequests();
            dispatch(getBookingDetail(leaseId + "?moveout=yes"));
          }
        } catch (error) {
          message.error(error.response?.data?.error || error.response?.data?.message);
        }
      },
      okText: "Confirm",
    });
  };

  const rejectMoveOutSummery = async (data) => {
    let body = {
      settlementApproval: "rejected",
      rejectionReason: data?.rejectReason ?? "",
      onlyBooking: true,
    };

    try {
      const response: any = await dispatch(updateBooking({ leaseId, body: body }));

      if (response?.data?.code === 200) {
        await refreshPendingRequests();

        dispatch(getBookingDetail(leaseId + "?moveout=yes"));
        message.success(response?.data?.message);
        setModalvisible(false);
      }
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message);
    }
  };

  const popup = [
    {
      title: (
        <Title level={3} className="mb-0">
          Reject Reason
        </Title>
      ),
      width: 650,
      content: <RejectPopup onSubmit={rejectMoveOutSummery} onClose={() => setModalvisible(false)} />,
    },
  ];

  const popupProps = {
    closable: true,
    visibility: modalvisible,
    title: popup[modalTrigger]?.title,
    content: popup[modalTrigger].content,
    width: popup[modalTrigger].width,
    onCancel: () => setModalvisible(false),
  };

  const autoPayDisable = () => {
    let body = {
      disableAutoPay: true,
      onlyBooking: true,
    };
    PopupConfirm({
      title: "Are you sure you want to cancel autopay?",
      content: "",
      onOk: async () => {
        try {
          const response: any = await dispatch(updateBooking({ leaseId, body: body }));

          if (response?.data?.code === 200) {
            message.success(response?.data?.message);
            dispatch(getBookingDetail(leaseId));
          }
        } catch (error) {
          message.error(error.response?.data?.error || error.response?.data?.message);
        }
      },
      okText: "Confirm",
    });
  };

  let moveOutScreenStatus = state?.moveOut || props.booking?.moveOutStatus;

  const RENEWAL_TYPES = new Set(["renewal", "extension"]);
  const BLOCKED_RENEWAL_STATUSES = new Set(["to_review", "sent_for_approval"]);

  const isRenewal = RENEWAL_TYPES.has(leaseType) || Boolean(state?.renewal);
  const showRentPerYear = !isRenewal || (renewalStatus && !BLOCKED_RENEWAL_STATUSES.has(renewalStatus));
  const isSmallHeader = !screens.xxl;

  const showEarlyTerminationApproval =
    props?.booking?.earlyTerminationRequestStatus === "pending" &&
    (((props?.booking?.leaseStatus === "active" || props?.booking?.leaseStatus === "lease_expired") &&
      checkUserRole(props?.booking?.loginUserType)) ||
      (isRenewal && props?.booking?.loginUserType === "adminManagersManager"));

  const renderActionChip = ({ key, icon, label, onClick, marginLeft }: ActionChip) => (
    <Space key={key} className="history-container" onClick={onClick} style={{ letterSpacing: "1.2px", marginLeft }}>
      {icon}
      <p className="history-label">{label}</p>
    </Space>
  );

  const renderActions = (isMobile = false) => {
    const nextSpacing = isMobile ? 0 : "10px";
    const firstSpacing = isMobile ? 0 : !isLeaseManager ? 0 : "10px";

    return (
      <>
        {[
          {
            key: "change-history",
            icon: <History width="18px" height="18px" style={{ marginTop: "4px", marginRight: "4px" }} />,
            label: "Change History",
            onClick: () => props.handleChangeHistory("11"),
            marginLeft: firstSpacing,
            visible: true,
          },
          {
            key: "cancel-autopay",
            icon: <CurrencyExchangeIcon width="28px" height="28px" />,
            label: "Cancel Autopay",
            onClick: autoPayDisable,
            marginLeft: nextSpacing,
            visible: Boolean(props?.booking?.isAutoPayActive),
          },
          {
            key: "configuration",
            icon: <SettingOutlined />,
            label: "Configuration",
            onClick: () => props.handleChangeHistory("40"),
            marginLeft: nextSpacing,
            visible: true,
          },
          {
            key: "custom-fields",
            icon: <FormOutlined />,
            label: "Custom Fields",
            onClick: () => props.handleChangeHistory("custom-fields"),
            marginLeft: nextSpacing,
            visible: Boolean(organization?.enableCustomFields),
          },
          {
            key: "financial-dimensions",
            icon: <BankOutlined />,
            label: "Financial Dimensions",
            onClick: () => props.handleChangeHistory("financial-dimensions"),
            marginLeft: nextSpacing,
            visible: Boolean(organization?.enableFinancialDimensions),
          },
        ]
          .filter((action) => action.visible)
          .map((action) => renderActionChip(action))}
      </>
    );
  };

  return (
    <Col span={24} className="cc-main view-booking">
      {!isBooking && (
        <Row gutter={24} className="loading">
          <Spin size="large" />
        </Row>
      )}

      {isBooking && (
        <>
          <Row gutter={12} style={rowStyle} className="row">
            <p className="ft-main-label">
              {`${props.booking?.propertyDetails?.unitNumber} ${props.booking?.propertyDetails?.propertyName} | ${
                props.booking?.propertyDetails?.unitCode
              }${props?.booking?.bookingId ? ` | ${label()} - ${props.booking?.bookingId}` : ""}`}
            </p>
            <div>
              {showRentPerYear && !props?.isRenewalToRenewal && (
                <p className="date">
                  {formatDateRange(
                    props?.booking?.leaseTerms?.leaseStartDate,
                    props?.booking?.leaseTerms?.leaseEndDate,
                  )}
                </p>
              )}
              {/* according to story 8455 if vacating date have */}
              {props?.booking?.vacatingDate && (
                <p className="date">( Vacating {formatDate(props?.booking?.vacatingDate)} )</p>
              )}
            </div>
          </Row>

          <Row gutter={12} style={rowStyleWithTopmargin} className="row">
            <Row>
              <ItemCard label={props?.booking?.propertyDetails?.usagesType} icon={Residential} />
              <ItemCard label={props?.booking?.propertyDetails?.unitType} icon={Apartment} />
              {props?.booking?.propertyDetails?.unitSubType?.includes("BR") && (
                <ItemCard
                  label={`${props?.booking?.propertyDetails?.unitSubType?.split("BR")[0]} Bedroom`}
                  icon={Bed}
                />
              )}
              <ItemCard label={`${props?.booking?.propertyDetails?.areaSqft} sqft`} icon={Measure} />
            </Row>

            {showRentPerYear && !props?.isRenewalToRenewal && (
              <Row gutter={12} className="row">
                <RentPerYear booking={props?.booking} />
              </Row>
            )}
          </Row>

          <Col span={24} className="ft-divider" />

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
            <div style={{ display: "flex", alignItems: "center", flex: 1, minWidth: 0, gap: 4 }}>
              {props.booking?.leaseManager?.name && (
                <div style={{ display: "flex", alignItems: "center", minWidth: "fit-content" }}>
                  <Row className="user">
                    <p className="user-label">{leaseExecutiveName(props.booking?.leaseManager?.name)}</p>
                  </Row>

                  <p className="user-name">{props.booking?.leaseManager?.name}</p>
                  <p className="user-executive">{"( Lease Executive )"}</p>
                </div>
              )}
              {props.booking?.adminManager?.name && (
                <div style={{ display: "flex", alignItems: "center", minWidth: "fit-content" }}>
                  <Row className="user">
                    <p className="user-label">{leaseExecutiveName(props.booking?.adminManager?.name)}</p>
                  </Row>

                  <p className="user-name">{props.booking?.adminManager?.name}</p>
                  <p className="user-executive">{"( Property Admin )"}</p>
                </div>
              )}

              {!isSmallHeader && <div>{renderActions()}</div>}
            </div>
            <div style={{ marginLeft: "auto", flex: "0 0 auto", display: "flex", justifyContent: "flex-end" }}>
              <Row gutter={8} className="status-container" style={{ justifyContent: "flex-end" }}>
                <p className="status-label">
                  {moveOutScreenStatus
                    ? formatString(props.booking?.moveOutStatus)
                    : props?.stateRenewal || props?.booking?.leaseType === "renewal"
                      ? formatString(
                          statusLabel(
                            props.renewal && !props.booking?.renewalStatus ? "to_review" : diffLeaseStatus(),
                            props?.booking?.leaseStatusHistory,
                          ),
                        ) || formatString(statusLabel(diffLeaseStatus(), props?.booking?.leaseStatusHistory))
                      : formatString(props.booking?.leaseStatus)}
                </p>
              </Row>
            </div>
          </div>

          {isSmallHeader && (
            <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "10px", marginTop: "12px" }}>
              {renderActions(true)}
            </div>
          )}

          {props?.booking?.settlementApproval &&
            props?.booking?.settlementApproval === "pending" &&
            checkUserRole(props?.booking?.loginUserType) && (
              <Tag className="fm-status-header" color="cyan" style={{ marginTop: "10px" }}>
                <div>
                  {
                    <ApprovalHeader
                      title={"Approval required for move-out summary"}
                      approve={moveOutSummeryApproval}
                      reject={() => {
                        setModalTrigger(0);
                        setModalvisible(true);
                      }}
                    />
                  }
                </div>
              </Tag>
            )}
          {showEarlyTerminationApproval && <EarlyTerminationApprovalActions />}
        </>
      )}
      <Popup destroyOnClose={true} {...popupProps} />
    </Col>
  );
};

export default BookingDetailsCard;
